import { useLayoutEffect, useRef, useState } from 'react';

const MIN_FONT_SIZE = 12;
// When the value's line height is 'normal'
const DEFAULT_LINE_HEIGHT = 1.2;

// The headline value's font size, at most maxFontSize but shrunk to fit what
// its wrapper has left once the rows around it (comparison, progress bar,
// captions) take their share: by width for long values ("13,274 / 88") and
// by height for short blocks. Remeasured as the wrapper or its rows resize,
// rows come or go, or the value's text changes (fitKey).
const useFittedFontSize = ({
  maxFontSize,
  fitKey,
  fitsHeight = true,
}: {
  maxFontSize: number;
  // Changes whenever what's shown changes, e.g. the formatted values
  fitKey: string;
  // Off where the wrapper is as tall as its content (it'd only ever fit what
  // it already is): the value then fits by width alone
  fitsHeight?: boolean;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);
  // The wrapper's row holding the value, where it shares its line (e.g. with
  // a comparison caption). The value itself when unset.
  const lineRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<number>(maxFontSize);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const fit = () => {
      const value = valueRef.current;
      if (!value) {
        setFontSize(maxFontSize);
        return;
      }
      const style = window.getComputedStyle(wrapper);
      const width =
        wrapper.clientWidth -
        parseFloat(style.paddingLeft || '0') -
        parseFloat(style.paddingRight || '0');
      const height =
        wrapper.clientHeight -
        parseFloat(style.paddingTop || '0') -
        parseFloat(style.paddingBottom || '0');
      // Not laid out (e.g. hidden, or in tests)
      if (width <= 0 || height <= 0) {
        setFontSize(maxFontSize);
        return;
      }
      const valueStyle = window.getComputedStyle(value);
      const current = parseFloat(valueStyle.fontSize);
      const gap = parseFloat(style.rowGap || '0') || 0;
      const line = lineRef.current ?? value;
      const others = Array.from(wrapper.children).filter((c) => c !== line);
      const othersHeight =
        others.reduce((sum, c) => {
          const otherStyle = window.getComputedStyle(c);
          return (
            sum +
            (c as HTMLElement).offsetHeight +
            (parseFloat(otherStyle.marginTop) || 0) +
            (parseFloat(otherStyle.marginBottom) || 0)
          );
        }, 0) +
        gap * others.length;
      // Whatever shares the value's line keeps its width. Taken from the
      // value's drawn width, not its text's: a value the line squeezes is
      // narrower than its text, which would hide what's beside it.
      const besideWidth =
        line === value ? 0 : Math.max(0, line.scrollWidth - value.offsetWidth);
      // Line height as a share of the font size. From its style, not its
      // height: the flex column squeezes a value that doesn't fit.
      const lineHeightPx = parseFloat(valueStyle.lineHeight);
      const lineHeight =
        current > 0 && Number.isFinite(lineHeightPx)
          ? lineHeightPx / current
          : DEFAULT_LINE_HEIGHT;
      const byWidth =
        value.scrollWidth > 0
          ? (current * (width - besideWidth)) / value.scrollWidth
          : maxFontSize;
      const byHeight = fitsHeight
        ? (height - othersHeight) / lineHeight
        : maxFontSize;
      const next = Math.floor(
        Math.max(MIN_FONT_SIZE, Math.min(maxFontSize, byWidth, byHeight)),
      );
      // A value that doesn't fit always shrinks, or it's cut off. Growing by
      // a pixel or less is left alone: refitting to rounding differences
      // would resize the value again, and again.
      setFontSize((prev) => (next > prev && next - prev <= 1 ? prev : next));
    };

    fit();
    if (
      typeof ResizeObserver === 'undefined' ||
      typeof MutationObserver === 'undefined'
    ) {
      return undefined;
    }
    // The wrapper and each row in it: rows change height, or come and go,
    // without the wrapper resizing (e.g. a subtext showing once its values
    // load)
    const resizes = new ResizeObserver(fit);
    const observeRows = () => {
      resizes.disconnect();
      resizes.observe(wrapper);
      Array.from(wrapper.children).forEach((child) => resizes.observe(child));
    };
    observeRows();
    const rows = new MutationObserver(() => {
      observeRows();
      fit();
    });
    rows.observe(wrapper, { childList: true });
    return () => {
      resizes.disconnect();
      rows.disconnect();
    };
  }, [fitKey, fitsHeight, maxFontSize]);

  return { fontSize, wrapperRef, valueRef, lineRef };
};

export default useFittedFontSize;
